import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getLPCRegionLOS from 'lib/offer/getLPCRegionLOS'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import { useAppSelector } from 'hooks/reduxHooks'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPriceNew'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import getOfferDurationText from 'lib/offer/getOfferDurationText'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import { EmptyObject } from 'lib/object/objectUtils'
import { isFlashHotelOffer, isHotelPackage } from 'lib/offer/offerTypes'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import getCharterBundleHotelOnlyDiscount from 'lib/offer/getCharterBundleHotelOnlyDiscount'
import { FlightsClassTypes, isRewardFareClass } from 'constants/flight'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG, MALDIVES_CHARTER_END_TARGET_DATE, MALDIVES_CHARTER_START_TARGET_DATE } from 'constants/charterFlight'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { resolveAllowedFareClass } from 'lib/charterFlights/charterOfferFareClassRestrictions'
import BodyText from 'LuxKit/Typography/BodyText'
import { computeRewardBundlePrice, REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'
import resolveLuxPlusMemberPrice from 'lib/search/resolveLuxPlusMemberPrice'
import RewardBundlePriceBlock from './RewardBundlePriceBlock'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.TourOffer | App.TourOfferSummary | App.VillaOffer | App.VillaOfferSummary
  selectedDatesAndRoomsFilter?: App.OfferListFilters
}

const defaultOccupancy: Array<App.Occupants> = [{ adults: 2 }]

function LEOfferForceBundlePricing(props: Props) {
  const {
    offer,
    selectedDatesAndRoomsFilter = EmptyObject,
  } = props
  const intl = useIntl()
  const perPersonSaleUnit = intl.formatMessage({
    id: '9puVBD3Kp2L5L-AmoMVK',
    defaultMessage: 'per person',
    description: 'Sale unit label for pricing that is shown on a per-person basis in offer pricing',
  })
  const totalSaleUnit = ''

  const bestPkg = useOffersBestPrices(offer, selectedDatesAndRoomsFilter)
  const pkg = bestPkg?.bestPricePackage ?? offer.lowestPricePackage!

  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)

  // Maldives charter bundle mode (homepage carousel)
  const { charterBundle: charterBundleFromContext, isPricePerPerson, fareClass, selectedFareClass: contextSelectedFareClass, useRewardPreviewPricing } = useMaldivesDirectBundleContext()
  const rooms = selectedDatesAndRoomsFilter.rooms ?? defaultOccupancy
  const totalPassengers = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const charterBundleRoute = useAppSelector(state => getCharterBundleRoute(state, offer))
  const selectedFareClass = fareClass ?? FlightsClassTypes.ECONOMY
  const effectiveFareClass = useMemo(() => {
    const resolvedFareClass = resolveAllowedFareClass({
      selectedFareClass,
      campaignTags: offer.campaignTags,
      occupancies: rooms,
    })
    return resolvedFareClass ?? selectedFareClass
  }, [selectedFareClass, offer.campaignTags, rooms])

  // Auto-detect charter bundle mode: if offer has charter campaign tag, treat as charter bundle
  // This ensures charter offers show bundle pricing (flights + hotel) even without context wrapper
  const isCharterBundleOffer = useMemo(() => {
    return isFlashHotelOffer(offer) &&
      offer.offerFlightsEnabled &&
      offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  }, [offer])
  const charterBundle = charterBundleFromContext || isCharterBundleOffer
  const shouldShowPerPersonPricing = charterBundleFromContext && isPricePerPerson

  const shouldFetchCharterFlightPrice = !!(
    charterBundle &&
    charterBundleRoute &&
    rooms.length
  )

  const isRewardSelected = useRewardPreviewPricing || (!!contextSelectedFareClass && isRewardFareClass(contextSelectedFareClass))
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const rewardLockedForLogin = isRewardSelected && !useRewardPreviewPricing && !memberId
  const {
    totalPrice: charterFlightPrice,
    regularTotalPrice: charterRegularFlightPrice,
    rewardSeatPointsTotal,
    rewardSeatCashCoPayTotal,
    rewardAvailable,
  } = useCharterFlightPrice({
    charterBundleRoute: charterBundleRoute ?? 'melbourne-maldives',
    checkInDate: selectedDatesAndRoomsFilter.checkIn ?? MALDIVES_CHARTER_START_TARGET_DATE,
    checkOutDate: selectedDatesAndRoomsFilter.checkOut ?? MALDIVES_CHARTER_END_TARGET_DATE,
    occupancies: rooms,
    fareClass: isRewardSelected && contextSelectedFareClass ? contextSelectedFareClass : effectiveFareClass,
    disabled: !shouldFetchCharterFlightPrice || useRewardPreviewPricing,
  })

  const directSearchPrices = useDirectSearchPrices({ filters: selectedDatesAndRoomsFilter, offerId: offer.id })

  // The charter flight leg carries no discount of its own, so source the pill from the
  // offer's hotel-only pricing instead of the bundled (flight-inclusive) price/value.
  const { discountPercent: charterHotelDiscountPercent, memberDiscountPercent: charterHotelMemberDiscountPercent } = getCharterBundleHotelOnlyDiscount(offer)

  const bundleComputed = useMemo(() => {
    if (!charterBundle) return undefined
    if (!charterBundleRoute) return undefined

    // Prefer direct search pricing; fallback to package pricing.
    const roomMultiplier = rooms.length || 1
    const packageHotelTotal = (pkg.price ?? 0) * roomMultiplier
    const packageMemberHotelTotal = (pkg.memberPrice ?? 0) * roomMultiplier
    const hotelTotalPrice = directSearchPrices?.lowestPrice ?? packageHotelTotal
    const resolvedMemberHotelTotal = resolveLuxPlusMemberPrice({
      metadataLowestMemberPrice: directSearchPrices?.lowestMemberPrice,
      referencePrice: directSearchPrices?.lowestPrice ?? pkg.price,
      packagePrice: pkg.price,
      packageMemberPrice: pkg.memberPrice,
      fallbackMemberPrice: pkg.memberPrice,
    })
    const hotelTotalMemberPrice = useRewardPreviewPricing
      ? (resolvedMemberHotelTotal ?? (packageMemberHotelTotal > 0 ? packageMemberHotelTotal : 0))
      : (resolvedMemberHotelTotal ?? packageMemberHotelTotal)
    const pkgValueOrMemberValue = canViewLuxPlusBenefits ? (pkg.memberValue ?? pkg.value ?? 0) : (pkg.value ?? 0)
    const hotelTotalValue = directSearchPrices?.lowestPriceValue ?? (pkgValueOrMemberValue * roomMultiplier)

    const flightTotalPrice = charterFlightPrice ?? 0
    const regularFlightTotalPrice = charterRegularFlightPrice ?? flightTotalPrice
    const totalPrice = hotelTotalPrice + regularFlightTotalPrice
    const totalMemberPrice = hotelTotalMemberPrice > 0 ? (hotelTotalMemberPrice + flightTotalPrice) : undefined
    const totalValue = hotelTotalValue + regularFlightTotalPrice

    const divisor = shouldShowPerPersonPricing && totalPassengers > 0 ? totalPassengers : 1
    const price = totalPrice / divisor
    const memberPrice = totalMemberPrice !== undefined ? (totalMemberPrice / divisor) : undefined
    const value = totalValue / divisor

    const discountPercent = charterHotelDiscountPercent ?? getValueOffPercent(value, price)
    const memberDiscountPercent = charterHotelMemberDiscountPercent
      ?? (memberPrice !== undefined ? getValueOffPercent(value, memberPrice) : undefined)

    return {
      price,
      memberPrice,
      value,
      discountPercent,
      memberDiscountPercent,
      saleUnit: shouldShowPerPersonPricing ? perPersonSaleUnit : totalSaleUnit,
      hotelTotalPrice,
      hotelTotalMemberPrice: hotelTotalMemberPrice > 0 ? hotelTotalMemberPrice : undefined,
    }
  }, [
    canViewLuxPlusBenefits,
    charterBundle,
    charterBundleRoute,
    charterFlightPrice,
    charterRegularFlightPrice,
    charterHotelDiscountPercent,
    charterHotelMemberDiscountPercent,
    directSearchPrices?.lowestMemberPrice,
    directSearchPrices?.lowestPrice,
    directSearchPrices?.lowestPriceValue,
    useRewardPreviewPricing,
    pkg.memberPrice,
    pkg.memberValue,
    pkg.price,
    pkg.value,
    perPersonSaleUnit,
    rooms.length,
    shouldShowPerPersonPricing,
    totalSaleUnit,
    totalPassengers,
  ])

  const flightOrigin = useAppSelector(state => getDefaultAirportCode(state))
  const flightPrice = offer.flightPrices[flightOrigin || '']
  const {
    withFlights: pricingWithFlights,
  } = useOfferPackagePrice(pkg, offer, { flightPrice })

  const showRewardPriceForElement = isRewardSelected && !rewardLockedForLogin && rewardAvailable && bundleComputed && rewardSeatPointsTotal != null && rewardSeatCashCoPayTotal != null
  const previewRewardRate = useRewardPreviewPricing ? REWARD_PREVIEW_RATE_BY_CABIN[effectiveFareClass] : undefined

  // Hooks must precede the conditional early return below — keep this useMemo here so
  // its call order is stable across renders even when `bundleComputed`/`pricingWithFlights` is missing.
  const rewardPriceElement = useMemo(() => {
    if (!showRewardPriceForElement || !bundleComputed || rewardSeatPointsTotal == null || rewardSeatCashCoPayTotal == null) return null
    const rewardPrice = computeRewardBundlePrice({
      hotelPrice: bundleComputed.hotelTotalPrice,
      hotelMemberPrice: bundleComputed.hotelTotalMemberPrice,
      rewardPointsTotal: rewardSeatPointsTotal,
      rewardCashCoPayTotal: rewardSeatCashCoPayTotal,
    })
    const isMemberReward = rewardPrice.memberCash !== undefined
    const divisor = shouldShowPerPersonPricing && totalPassengers > 0 ? totalPassengers : 1
    const rewardPointsDisplay = rewardPrice.points / divisor
    const rewardCashTotal = (rewardPrice.memberCash ?? rewardPrice.cash) / divisor
    const nonMemberCashTotal = rewardPrice.cash / divisor
    return <RewardBundlePriceBlock
      points={rewardPointsDisplay}
      cash={rewardCashTotal}
      nonMemberCash={nonMemberCashTotal}
      isMemberReward={isMemberReward}
      saleUnit={bundleComputed.saleUnit}
      saleUnitDisplay="text"
      nonMemberDisplay="plain"
      testId="main-offer-pricing-reward"
    />
  }, [showRewardPriceForElement, bundleComputed, rewardSeatPointsTotal, rewardSeatCashCoPayTotal, shouldShowPerPersonPricing, totalPassengers])

  const rewardPreviewPriceElement = useMemo(() => {
    if (!bundleComputed || !previewRewardRate) return null

    const rewardPrice = computeRewardBundlePrice({
      hotelPrice: bundleComputed.hotelTotalPrice,
      hotelMemberPrice: bundleComputed.hotelTotalMemberPrice,
      rewardPointsTotal: previewRewardRate.pointsPerPerson * totalPassengers,
      rewardCashCoPayTotal: previewRewardRate.cashPerPerson * totalPassengers,
    })
    const divisor = shouldShowPerPersonPricing && totalPassengers > 0 ? totalPassengers : 1
    const isMemberReward = rewardPrice.memberCash !== undefined
    const rewardPoints = rewardPrice.points / divisor
    const rewardCashTotal = (rewardPrice.memberCash ?? rewardPrice.cash) / divisor
    const nonMemberCashTotal = rewardPrice.cash / divisor

    return <RewardBundlePriceBlock
      points={rewardPoints}
      cash={rewardCashTotal}
      nonMemberCash={nonMemberCashTotal}
      isMemberReward={isMemberReward}
      saleUnit={bundleComputed.saleUnit}
      saleUnitDisplay="slash"
      nonMemberDisplay="bold"
      testId="main-offer-pricing-reward-preview"
      showLuxPlusLogo
    />
  }, [bundleComputed, previewRewardRate, shouldShowPerPersonPricing, totalPassengers])

  if (!bundleComputed && !pricingWithFlights) {
    return null
  }

  const totalPriceWithFlights = bundleComputed?.price ?? pricingWithFlights!.price
  const memberPriceWithFlights = bundleComputed?.memberPrice
  const displayTotalValue = bundleComputed?.value ?? pricingWithFlights!.value
  const discountPercent = bundleComputed?.discountPercent ?? pricingWithFlights!.discountPercent
  const valueDiscountPercent = (canViewLuxPlusBenefits && bundleComputed?.memberDiscountPercent !== undefined)
    ? bundleComputed.memberDiscountPercent
    : discountPercent

  const shouldShowRate = !offer.isDiscountPillHidden && discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const shouldShowValue = pkg.value > 0 && totalPriceWithFlights && displayTotalValue > totalPriceWithFlights && pkg.shouldDisplayValue &&
    (isFlashHotelOffer(offer) || (isHotelPackage(pkg) && !pkg.roomRate?.inclusionsHideValue))

  let staysFrom = offer.minDuration
  const LPCRegionLOS = getLPCRegionLOS(offer)
  if (LPCRegionLOS && offer.packages.some(p => p.duration === LPCRegionLOS)) {
    staysFrom = LPCRegionLOS
  }
  const showStaysFromLabel = staysFrom && offer.type === OFFER_TYPE_ALWAYS_ON && LPCRegionLOS && staysFrom < pkg.duration

  const durationText = bundleComputed
    ? intl.formatMessage(
        {
          id: 'gEFgViLaxzHaUoh9q6IV',
          defaultMessage: '{count, plural, one {# night} other {# nights}} & flights from',
          description: 'Bundle offer duration in nights with flights, shown as price caption on offer card',
        },
        { count: pkg.duration },
      )
    : getOfferDurationText(intl, offer, pkg)

  const priceElement = rewardLockedForLogin ? (
    <BodyText variant="medium" colour="neutral-two">
      {intl.formatMessage({
        id: 'zp0O9z2ci6Dk9LPEDw0u',
        defaultMessage: 'Log in to view',
        description: 'Message shown on reward fare cards when the user is not logged in',
      })}
    </BodyText>
  ) : (useRewardPreviewPricing
    ? (rewardPreviewPriceElement ?? (bundleComputed ? (
        <LuxPlusPriceStack
          price={totalPriceWithFlights}
          memberPrice={memberPriceWithFlights}
          size="L"
          saleUnit={bundleComputed.saleUnit}
          offerConfig={offer.luxPlus}
          testid="main-offer-pricing"
        />
      ) : (
        <PriceRowPrice
          price={totalPriceWithFlights}
          saleUnit={pricingWithFlights!.saleUnit}
          size="L"
        />
      )))
    : (rewardPreviewPriceElement ?? rewardPriceElement ?? (bundleComputed ? (
        <LuxPlusPriceStack
          price={totalPriceWithFlights}
          memberPrice={memberPriceWithFlights}
          size="L"
          saleUnit={bundleComputed.saleUnit}
          offerConfig={offer.luxPlus}
          testid="main-offer-pricing"
        />
      ) : (
        <PriceRowPrice
          price={totalPriceWithFlights}
          saleUnit={pricingWithFlights!.saleUnit}
          size="L"
        />
      ))))

  return (<>
    <PriceRowPriceCaption>{durationText}</PriceRowPriceCaption>
    {priceElement}
    {shouldShowValue && <PriceRowValueDiscountWithCaption
      size="L"
      originalValue={Math.ceil(displayTotalValue)}
      discountPercentage={shouldShowRate ? valueDiscountPercent : undefined}
    />}
    {staysFrom && showStaysFromLabel && <PriceRowCaption>
      <FormattedMessage
        id="7PFPr3tSopW8Hgmmb_Dy"
        defaultMessage="Stays from {staysFrom, plural, one {# night} other {# nights}} available"
        description="Caption indicating minimum stay duration available for an always-on offer"
        values={{ staysFrom }}
      />
    </PriceRowCaption>}
    {!!pkg.roomOccupancy && offer.type === 'tour' && <PriceRowCaption>
      {pkg.roomOccupancy}
    </PriceRowCaption>}
  </>)
}

export default LEOfferForceBundlePricing
